<template>
  <div v-editable="blok" class="storyblok-faq-entry">
    <EMAccordion :title="questionLabel" :border="true">
      <template #header>
        <!-- <span class="storyblok-faq-entry__question">{{ questionLabel }}</span> -->
        <StoryblokRichText
          v-if="richTextQuestion"
          :blok="richTextQuestion"
          class="storyblok-faq-entry__question"
        />
        <p v-else-if="plainTextQuestion" class="storyblok-faq-entry__answer-text">
          {{ plainTextQuestion }}
        </p>
      </template>

      <div class="storyblok-faq-entry__answer">
        <StoryblokRichText v-if="richTextAnswer" :blok="richTextAnswer" />
        <p v-else-if="plainTextAnswer" class="storyblok-faq-entry__answer-text">
          {{ plainTextAnswer }}
        </p>
      </div>
    </EMAccordion>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import EMAccordion from '@/common/components/ui/accordion/EMAccordion.vue';
import StoryblokRichText from '@/features/storyblok/components/StoryblokRichText.vue';
import type { StoryblokFaqEntryBlok, StoryblokRichTextBlok } from '@/features/storyblok/types';

const props = defineProps<{
  blok: StoryblokFaqEntryBlok;
}>();

const extractPlainText = (value: unknown): string => {
  if (!value || typeof value !== 'object') {
    return '';
  }

  const text = 'text' in value && typeof value.text === 'string' ? value.text : '';
  const nestedText =
    'content' in value && Array.isArray(value.content)
      ? value.content.map((child) => extractPlainText(child)).join(' ')
      : '';

  return [text, nestedText].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
};

const questionLabel = computed(() =>
  typeof props.blok.question === 'string'
    ? props.blok.question.trim()
    : extractPlainText(props.blok.question)
);
const richTextQuestion = computed<StoryblokRichTextBlok | null>(() =>
  typeof props.blok.question === 'string' || !props.blok.question
    ? null
    : {
        content: props.blok.question,
      }
);
const plainTextQuestion = computed(() =>
  typeof props.blok.question === 'string' ? props.blok.question.trim() : undefined
);

const richTextAnswer = computed<StoryblokRichTextBlok | null>(() =>
  typeof props.blok.answer === 'string' || !props.blok.answer
    ? null
    : {
        content: props.blok.answer,
      }
);

const plainTextAnswer = computed(() =>
  typeof props.blok.answer === 'string' ? props.blok.answer.trim() : undefined
);
</script>

<style scoped>
.storyblok-faq-entry {
  width: 100%;
}

.storyblok-faq-entry__question {
  display: inline-flex;
  flex: 1;
  align-items: center;
  min-height: 44px;
  font-weight: 600;
  color: var(--ion-text-color);
  padding: 0 var(--ion-space-10);
}

.storyblok-faq-entry__answer {
  width: 100%;
  padding: 0 var(--ion-space-10);
}

.storyblok-faq-entry__answer :deep(.rich-text) {
  color: var(--ion-text-color);
}

.storyblok-faq-entry__answer-text {
  margin: 0;
  color: var(--ion-text-color);
}
</style>
